import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button as BsButton, Modal } from "react-bootstrap";
import { Button, ConditionalTooltip } from "../../../../../../components";
import { consts } from "../../../../../../shim/jsbridge";
import { isAdmin } from "../../../../../../utils";
import { SaveAsForm as SaveAsFormType } from "../../@types";
import {
  useCreateLayout,
  useGetLayouts,
  useGridRowsContext
} from "../../hooks";
import { SaveAsForm } from "../forms";

const { max_number_of_user_layouts, max_number_of_shared_layouts } =
  consts ?? {};

type Props = {
  disabled?: boolean;
  onClick?: () => void;
};

const admin = isAdmin();

const getButtonDisabled = (count?: number) => {
  if (admin) return false;
  if (count === undefined) return true;
  return count >= max_number_of_user_layouts;
};

const getSubmitDisabled = (
  type: string,
  userCount?: number,
  sharedCount?: number
) => {
  if (userCount === undefined) return true;
  if (sharedCount === undefined) return true;
  if (type === "user" && userCount >= max_number_of_user_layouts) return true;
  if (type === "shared" && sharedCount >= max_number_of_shared_layouts)
    return true;
  return false;
};

const TYPE_MAP: Record<string, string> = {
  user: "personal",
  shared: "shared"
} as const;

const INITIAL_VALUES: SaveAsFormType = {
  name: "",
  type: "user"
} as const;

const CreateButton = ({ disabled, onClick }: Props) => {
  const [opened, setOpened] = useState(false);
  const [values, setValues] = useState(INITIAL_VALUES);

  const navigate = useNavigate();

  const { canvasRows } = useGridRowsContext();
  const { data } = useGetLayouts();
  const { create } = useCreateLayout();

  const openModal = () => {
    setOpened(true);
  };

  const closeModal = () => {
    setOpened(false);
    setValues(INITIAL_VALUES);
  };

  const handleFormChange = (value: SaveAsFormType) => {
    setValues(value);
  };

  const handleCreate = () => {
    onClick?.();
    create({ ...values, content: canvasRows }).then(({ id }) => {
      closeModal();
      setValues(INITIAL_VALUES);
      navigate(`/samples/layouts/${values.type}/${id}/`);
    });
  };

  const buttonDisabled = getButtonDisabled(data?.user.length);
  const submitDisabled = getSubmitDisabled(
    values.type,
    data?.user.length,
    data?.shared.length
  );

  return (
    <>
      <ConditionalTooltip
        tooltip={`Maximum number of personal layouts (${max_number_of_user_layouts})
              reached. Delete an existing layout to allow saving new ones.`}
        condition={buttonDisabled}
      >
        <div>
          <Button
            data-testid="layout-create-button"
            disabled={disabled || buttonDisabled}
            onClick={openModal}
            variant="primary"
          >
            Save As
          </Button>
        </div>
      </ConditionalTooltip>
      <Modal show={opened} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Save Layout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SaveAsForm value={values} onChange={handleFormChange} />
        </Modal.Body>
        <Modal.Footer>
          <BsButton onClick={closeModal} variant="prompt-secondary">
            CANCEL
          </BsButton>
          <ConditionalTooltip
            condition={submitDisabled}
            tooltip={`Maximum number of ${TYPE_MAP[values.type]} layouts (${
              values.type === "user"
                ? max_number_of_user_layouts
                : max_number_of_shared_layouts
            }) reached. Delete an existing layout to allow saving new ones.`}
          >
            <div style={{ marginLeft: "10px" }}>
              <BsButton
                disabled={submitDisabled || !values.name}
                variant="prompt-primary"
                onClick={handleCreate}
              >
                SAVE
              </BsButton>
            </div>
          </ConditionalTooltip>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateButton;
